import { useState, useEffect, useRef, useMemo } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { fetchDashboardSummary, fetchBranches } from '../../services/salesInvoiceService';
import {
    TrendingUp, BarChart3, Wallet, CreditCard, Users, RefreshCw, LayoutDashboard,
    Crown, PackageSearch, Building2, FileText, Megaphone, Search, ChevronDown
} from 'lucide-react';
import { KPICard } from './components/KPICard';
import { RevenueTrendCard } from './components/RevenueTrendCard';
import { TopStatsCard } from './components/TopStatsCard';
import { SellerPerformanceCard } from './components/SellerPerformanceCard';
import { SalesProgressCard } from './components/SalesProgressCard';
import { cardStyle, glassCard } from '../../helpers/Executive-Dashboard/styleFormat';
import { fmt, fmtNum, fmtCompact } from '../../helpers/Executive-Dashboard/formatters';
import { integration } from '../../helpers/Executive-Dashboard/integration';

/* ── MAIN COMPONENT ─────────────────────────────────────────────── */
const ExecutiveDashboard = () => {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState('PT. Suma Alpha Indonesia');
    const [branches, setBranches] = useState(null);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    const [isSubDropdownOpen, setIsSubDropdownOpen] = useState(false);
    const [subSearchTerm, setSubSearchTerm] = useState('');
    const subDropdownRef = useRef(null);
    const [subFilter, setSubFilter] = useState('ALL');
    const [sellerViewModes, setSellerViewModes] = useState({});

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (subDropdownRef.current && !subDropdownRef.current.contains(event.target)) {
                setIsSubDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [runningText, setRunningText] = useState('');

    useEffect(() => {
        if (filter === 'Gelora Printing') {
            loadData(filter, subFilter, 'GP');
        } else if (filter === 'PT. Suma Alpha Indonesia') {
            loadData(filter, subFilter, 'SUMA');
        }
    }, [filter, subFilter]);

    useEffect(() => {
        const controller = new AbortController();
        fetchBranch();
        return () => controller.abort();
    }, []);

    const contentRunningText = (value, branchSelected) => {
        let gpText = 'Data Gelora Printing Diambil Dari Wilayah Bandung - Surabaya - Bali';
        if (branchSelected?.length && value != 'PT. Suma Alpha Indonesia') {
            const branchNames = branchSelected
                .map(b => b.label.charAt(0).toUpperCase() + b.label.slice(1).toLowerCase())
                .join(' - ');
            gpText = `Data Gelora Printing Diambil Dari Wilayah ${branchNames}`;
        }

        const content = {
            'ALL': `Data PT. Suma Alpha Indonesia Diambil Dari Semua Cabang & ${gpText}`,
            'PT. Suma Alpha Indonesia': 'Data PT. Suma Alpha Indonesia Diambil Dari Semua Cabang',
            'Gelora Printing': gpText,
        };

        setRunningText(content[value]);
    }

    const fetchBranch = async () => {
        try {
            const config = integration('GP');
            const branchResult = await fetchBranches(config);
            setBranches(branchResult);
        } catch (error) {
            setError(error.message);
        }
    }

    const loadData = async (filter, currentSubFilter, selectedConfig) => {
        setLoading(true);
        setError(null);
        try {
            const branch = currentSubFilter || subFilter;
            const config = integration(selectedConfig);
            const resultData = await fetchDashboardSummary(config, branch);
            setData([{ [filter]: resultData }]);

            contentRunningText(filter, resultData?.branchSelected);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const hardcodedSubFilterOptions = [
        { value: 'ALL', label: 'Semua Cabang' },
        { value: 'Jakarta', label: 'Jakarta' },
        { value: 'Bandung', label: 'Bandung' },
        { value: 'Surabaya', label: 'Surabaya' },
        { value: 'Ambon', label: 'Ambon' },
        { value: 'Yogyakarta', label: 'Yogyakarta' },
    ];

    const subFilterOptions = useMemo(() => {
        const apiOptions = branches ?? [];
        const existingValues = new Set(hardcodedSubFilterOptions.map(o => o.value.toLowerCase()));
        const newFromApi = apiOptions
            .filter(o => !existingValues.has(o.value.toLowerCase()))
            .map(o => ({ ...o, label: o.label.charAt(0).toUpperCase() + o.label.slice(1).toLowerCase() }));
        return [...hardcodedSubFilterOptions, ...newFromApi];
    }, [branches]);

    const { sources, totalKpis, multi } = useMemo(() => {
        if (!data) return { sources: [], totalKpis: {}, multi: false };

        const filtered = data.filter(item => Object.keys(item)[0] === filter);

        const mappedSources = filtered.map(item => {
            const [label, value] = Object.entries(item)[0];
            return { label, ...value };
        });

        const calcKpis = filtered.reduce((acc, item) => {
            const { kpis } = Object.values(item)[0];
            if (kpis) {
                Object.keys(kpis).forEach(key => {
                    acc[key] = (acc[key] || 0) + (kpis[key] || 0);
                });
            }
            return acc;
        }, {});

        return { sources: mappedSources, totalKpis: calcKpis, multi: mappedSources.length > 1 };
    }, [data, filter]);

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: '16px' }}>
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                <span style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>Loading Executive Dashboard…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ color: '#ef4444', fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Failed to load data</div>
                <div style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '16px' }}>{error}</div>
                <button onClick={() => filter === 'Gelora Printing' ? loadData(filter, subFilter, 'GP') : loadData(filter, subFilter, 'SUMA')} style={{ padding: '8px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Retry</button>
            </div>
        );
    }

    if (!data) return null;

    const filterOptions = [
        { value: 'PT. Suma Alpha Indonesia', label: 'PT. Suma Alpha Indonesia' },
        { value: 'Gelora Printing', label: 'Gelora Printing' },
    ];

    const departmentKey = {
        JKT: 'Jakarta',
        SBY: 'Surabaya',
        BDG: 'Bandung',
        SMG: 'Semarang',
        PLG: 'Palembang',
        SMR: 'Samarinda',
        MDN: 'Medan',
        YYK: 'Yogyakarta'
    }

    return (
        <div style={{ padding: '16px 20px', maxWidth: '1440px', margin: '0 auto' }}>
            <style>{`
                @keyframes scroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-100%); }
                }
                @keyframes pulse-icon {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.15); opacity: 0.8; }
                    100% { transform: scale(1); opacity: 1; }
                }
                .running-text-wrapper {
                    max-width: 400px;
                    width: 100%;
                    margin: 0 24px;
                    background: #f1f5f9;
                    border-radius: 50px;
                    padding: 6px 16px;
                    display: flex;
                    align-items: center;
                    overflow: hidden;
                    position: relative;
                    border: 1px solid #e2e8f0;
                    box-shadow: inset 0 2px 4px rgba(0,0,0,0.05), inset 0 -1px 1px rgba(255,255,255,0.5);
                }
                .running-text-label {
                    color: #3b82f6;
                    font-size: 11px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding-right: 12px;
                    position: relative;
                    z-index: 2;
                    border-right: 1px solid #cbd5e1;
                    margin-right: 12px;
                }
                .ticker-icon {
                    animation: pulse-icon 2s ease-in-out infinite;
                }
                .running-text-content {
                    flex: 1;
                    overflow: hidden;
                    mask-image: linear-gradient(90deg, transparent 0%, black 5%, black 95%, transparent 100%);
                    -webkit-mask-image: linear-gradient(90deg, transparent 0%, black 5%, black 95%, transparent 100%);
                    display: flex;
                    align-items: center;
                }
                .running-text-scroll {
                    white-space: nowrap;
                    animation: scroll 20s linear infinite;
                    color: #0f172a;
                    font-size: 13px;
                    font-weight: 600;
                    display: inline-block;
                    padding-left: 100%;
                }
                .running-text-wrapper:hover .running-text-scroll {
                    animation-play-state: paused;
                }
                @media (max-width: 1024px) {
                    .running-text-wrapper {
                        order: 3;
                        flex: 1 1 100%;
                        margin: 8px 0 0 0;
                    }
                }
            `}</style>

            {/* ═══ HEADER ═══ */}
            <div style={{
                ...glassCard, padding: '16px 24px', marginBottom: '16px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px',
                borderTop: '3px solid #3b82f6', position: 'relative', zIndex: 30
            }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LayoutDashboard size={22} color="#3b82f6" /> Executive Sales Dashboard
                    </h2>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                        Tren Data Accurate dan Datadigi (5 Tahun Terakhir)
                    </div>
                </div>

                <div className="running-text-wrapper">
                    <div className="running-text-label">
                        <Megaphone size={14} className="ticker-icon" /> INFO
                    </div>
                    <div className="running-text-content">
                        <div className="running-text-scroll">
                            {runningText}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ position: 'relative' }} ref={dropdownRef}>
                        <div
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            style={{
                                padding: '8px 14px', borderRadius: '10px',
                                border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: '600',
                                color: '#1e293b', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
                                minWidth: '240px', transition: 'all 0.2s ease', gap: '16px'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Building2 size={14} color="#64748b" />
                                {filterOptions.find(opt => opt.value === filter)?.label || 'PT. Suma Alpha Indonesia'}
                            </div>
                            <ChevronDown size={14} color="#94a3b8" />
                        </div>

                        {isDropdownOpen && (
                            <div style={{
                                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                                background: '#fff', borderRadius: '10px',
                                border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                width: '100%', minWidth: '240px', zIndex: 50, overflow: 'hidden'
                            }}>
                                <div style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', background: '#f8fafc',
                                        borderRadius: '6px', border: '1px solid #e2e8f0', padding: '6px 10px',
                                        gap: '8px'
                                    }}>
                                        <Search size={14} color="#94a3b8" />
                                        <input
                                            type="text"
                                            placeholder="Cari..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{
                                                border: 'none', background: 'transparent', outline: 'none',
                                                fontSize: '12px', width: '100%', color: '#1e293b'
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                    {filterOptions
                                        .filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
                                        .map((opt, index) => (
                                            <div
                                                key={index}
                                                onClick={() => {
                                                    setFilter(opt.value);
                                                    if (opt.value !== 'Gelora Printing') {
                                                        setSubFilter('ALL');
                                                    }
                                                    const gpBranches = data?.find(item => item['Gelora Printing'])?.['Gelora Printing']?.branchSelected;
                                                    contentRunningText(opt.value, gpBranches);
                                                    setIsDropdownOpen(false);
                                                    setSearchTerm('');
                                                }}
                                                style={{
                                                    padding: '10px 14px', fontSize: '12px', color: '#475569', fontWeight: '500',
                                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                                                    background: filter === opt.value ? '#f8fafc' : 'transparent',
                                                    borderBottom: '1px solid #f8fafc', transition: 'background 0.2s'
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                                                onMouseOut={(e) => e.currentTarget.style.background = filter === opt.value ? '#f8fafc' : 'transparent'}
                                            >
                                                <Building2 size={13} color={filter === opt.value ? '#3b82f6' : '#94a3b8'} />
                                                <span style={{ color: filter === opt.value ? '#0f172a' : 'inherit', fontWeight: filter === opt.value ? '600' : '500' }}>{opt.label}</span>
                                            </div>
                                        ))
                                    }
                                    {filterOptions.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
                                        <div style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                                            Tidak ditemukan
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {filter === 'Gelora Printing' && (
                        <div style={{ position: 'relative' }} ref={subDropdownRef}>
                            <div
                                onClick={() => setIsSubDropdownOpen(!isSubDropdownOpen)}
                                style={{
                                    padding: '8px 14px', borderRadius: '10px',
                                    border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: '600',
                                    color: '#1e293b', background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
                                    minWidth: '200px', transition: 'all 0.2s ease', gap: '16px'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Building2 size={14} color="#64748b" />
                                    {subFilterOptions.find(opt => opt.value === subFilter)?.label || 'Semua Cabang'}
                                </div>
                                <ChevronDown size={14} color="#94a3b8" />
                            </div>

                            {isSubDropdownOpen && (
                                <div style={{
                                    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                                    background: '#fff', borderRadius: '10px',
                                    border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    width: '100%', minWidth: '200px', zIndex: 50, overflow: 'hidden'
                                }}>
                                    <div style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>
                                        <div style={{
                                            display: 'flex', alignItems: 'center', background: '#f8fafc',
                                            borderRadius: '6px', border: '1px solid #e2e8f0', padding: '6px 10px',
                                            gap: '8px'
                                        }}>
                                            <Search size={14} color="#94a3b8" />
                                            <input
                                                type="text"
                                                placeholder="Cari Cabang..."
                                                value={subSearchTerm}
                                                onChange={(e) => setSubSearchTerm(e.target.value)}
                                                style={{
                                                    border: 'none', background: 'transparent', outline: 'none',
                                                    fontSize: '12px', width: '100%', color: '#1e293b'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                        {subFilterOptions
                                            .filter(opt => opt.label.toLowerCase().includes(subSearchTerm.toLowerCase()))
                                            .map((opt, index) => {
                                                const apiOptions = branches ?? [];
                                                const isDisabled = opt.value !== 'ALL' && !apiOptions.some(o => o.value.toLowerCase() === opt.value.toLowerCase());
                                                return (
                                                    <div
                                                        key={index}
                                                        onClick={() => {
                                                            if (isDisabled) return;
                                                            setSubFilter(opt.value);
                                                            setIsSubDropdownOpen(false);
                                                            setSubSearchTerm('');
                                                        }}
                                                        style={{
                                                            padding: '10px 14px', fontSize: '12px',
                                                            color: isDisabled ? '#cbd5e1' : '#475569',
                                                            fontWeight: '500',
                                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                            display: 'flex', alignItems: 'center', gap: '8px',
                                                            background: subFilter === opt.value ? '#f8fafc' : 'transparent',
                                                            borderBottom: '1px solid #f8fafc', transition: 'background 0.2s'
                                                        }}
                                                        onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                                                        onMouseOut={(e) => e.currentTarget.style.background = subFilter === opt.value ? '#f8fafc' : 'transparent'}
                                                    >
                                                        <Building2 size={13} color={subFilter === opt.value ? '#3b82f6' : '#94a3b8'} />
                                                        <span style={{ color: subFilter === opt.value ? '#0f172a' : 'inherit', fontWeight: subFilter === opt.value ? '600' : '500' }}>{opt.label}</span>
                                                    </div>
                                                )
                                            })
                                        }
                                        {subFilterOptions.filter(opt => opt.label.toLowerCase().includes(subSearchTerm.toLowerCase())).length === 0 && (
                                            <div style={{ padding: '12px', fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
                                                Tidak ditemukan
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <button onClick={() => {
                        if (filter === 'Gelora Printing') {
                            loadData(filter, subFilter, 'GP');
                        } else {
                            loadData(filter, subFilter, 'SUMA');
                        }
                    }} style={{
                        padding: '8px 14px', fontSize: '12px', fontWeight: '600',
                        borderRadius: '10px', cursor: 'pointer', border: '1px solid #e2e8f0',
                        background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)', color: '#64748b',
                        display: 'flex', alignItems: 'center', gap: '6px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
                        transition: 'all 0.2s ease',
                    }}><RefreshCw size={14} /> Refresh</button>
                </div>
            </div>

            {/* ═══ KPI ROW ═══ */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                <KPICard label="Pendapatan Hari Ini" value={fmt(totalKpis.todayRevenue)} sub={`${fmtNum(totalKpis.todayInvoiceCount)} invoice`} color="#3b82f6" icon={<TrendingUp size={24} />} />
                <KPICard label="Pendapatan Bulan Ini" value={fmtCompact(totalKpis.monthRevenue)} sub={`${fmtNum(totalKpis.monthInvoiceCount)} invoice`} color="#10b981" icon={<BarChart3 size={24} />} />
                <KPICard label="Total Pendapatan" value={fmtCompact(totalKpis.totalRevenue)} sub={`${fmtNum(totalKpis.totalInvoices)} invoice`} color="#8b5cf6" icon={<Wallet size={24} />} />
                {filter !== 'Gelora Printing' && (
                    <KPICard label="Total Pembayaran" value={fmtCompact(totalKpis.totalPaid)} sub={`${fmtNum(totalKpis.totalPaymentCount)} transaksi`} color="#06b6d4" icon={<CreditCard size={24} />} />
                )}
                <KPICard label="Customer Aktif" value={fmtNum(totalKpis.uniqueCustomers)} sub={`Avg ${fmtCompact(totalKpis.totalInvoices > 0 ? totalKpis.totalRevenue / totalKpis.totalInvoices : 0)}/invoice`} color="#f97316" icon={<Users size={24} />} />
            </div>

            {/* ═══ REVENUE TREND (each source = own card) ═══ */}
            {sources.map((src, index) => (
                <RevenueTrendCard key={index} src={src} index={index} multi={multi} branch={src.label === 'Gelora Printing' ? subFilter : ''} />
            ))}

            {/* ═══ TOP CUSTOMERS + TOP PRODUCTS + PAYMENT (all sources in 1 section) ═══ */}
            {sources.map((src, index) => (
                <div key={index}>
                    {multi && (
                        <div style={{
                            fontSize: '13px', fontWeight: '700', color: '#3b82f6',
                            margin: '4px 0 10px', paddingLeft: '4px',
                        }}>
                            <Building2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> {src.label}
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        <TopStatsCard title="Top 10 Customer"
                            icon={<Crown size={16} style={{ color: '#f59e0b' }} />} data={src.topCustomers}
                            color="#10b981" sourceLabel={src.label} topType={'customer'} branch={src.label === 'Gelora Printing' ? subFilter : ''} />
                        <TopStatsCard title="Top 10 Produk"
                            icon={<PackageSearch size={16} style={{ color: '#3b82f6' }} />} data={src.topItems}
                            color="#3b82f6" sourceLabel={src.label} topType={'product'} branch={src.label === 'Gelora Printing' ? subFilter : ''} />
                        <div style={{ ...cardStyle, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: (sellerViewModes[src.label] || 'permonth') === 'all' ? '372px' : '400px' }}>
                            <SellerPerformanceCard sourceLabel={src.label} branch={src.label === 'Gelora Printing' ? subFilter : ''} onViewModeChange={(mode) => setSellerViewModes(prev => ({ ...prev, [src.label]: mode }))} />
                        </div>
                    </div>
                    {/* NEW SALES PROGRESS CARD */}
                    {(filter !== 'Gelora Printing' || (subFilter !== 'ALL' && subFilter !== 'Jakarta' && subFilter !== 'Ambon')) && (
                        <SalesProgressCard sourceLabel={src.label} branch={src.label === 'Gelora Printing' ? subFilter : ''} />
                    )}
                </div>
            ))}

            {/* ═══ RECENT INVOICES (each source = own card) ═══ */}
            {sources.map((src, index) => (
                <div key={index} style={{ ...cardStyle, marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', marginBottom: '14px' }}>
                        <FileText size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px', color: '#3b82f6' }} /> Invoice Terbaru {multi ? (<span style={{ color: '#94a3b8' }}>{`— ${src.label}`}</span>) : ''}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                    {['No. Invoice', 'Tanggal Faktur', 'Customer', 'Nilai Faktur', 'Jatuh Tempo', 'TOP',
                                        ...(src.label === 'PT. Suma Alpha Indonesia' ? ['Nama Sales', 'Departemen'] : ['Wilayah'])
                                    ].map(h => (
                                        <th key={h} style={{
                                            textAlign: h.includes('Nilai Faktur') || h === 'Diskon'
                                                || h === 'TOP' || h === 'Nama Sales' || h === 'Departemen' || h === 'Wilayah'
                                                ? 'right' : 'left', padding: '8px 10px', fontWeight: '600', color: '#64748b', fontSize: '11px'
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {src.recentInvoices.map((inv, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '8px 10px', fontWeight: '600', color: '#1e293b', fontFamily: 'monospace', fontSize: '11px' }}>{inv.NOMOR_INVOICE}</td>
                                        <td style={{ padding: '8px 10px', color: '#475569' }}>{inv.TANGGAL_INVOICE}</td>
                                        <td style={{ padding: '8px 10px', color: '#334155', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.NAMA_CUSTOMER}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>{fmt(inv.TOTAL || 0)}</td>
                                        <td style={{ padding: '8px 10px', color: '#64748b' }}>{inv.DUE_DATE}</td>
                                        {src.label === 'PT. Suma Alpha Indonesia' && (
                                            <>
                                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#94a3b8' }}>{inv.TERM_OF_PAYMENT === '0' || inv.TERM_OF_PAYMENT === 0 ? 'COD' : inv.TERM_OF_PAYMENT + 'd'}</td>
                                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#64748b' }}>{inv.SALESMAN_NAME}</td>
                                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#64748b' }}>{
                                                    inv.NOMOR_INVOICE ? (departmentKey[inv.NOMOR_INVOICE.split('/')[1].split('.')[0]] ?? '-') : '-'
                                                }</td>
                                            </>
                                        )}
                                        {src.label === 'Gelora Printing' && (
                                            <>
                                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#94a3b8' }}>
                                                    {
                                                        inv.NOMOR_INVOICE?.includes('/INV') ? '-'
                                                            : inv.TERM_OF_PAYMENT === '0' || inv.TERM_OF_PAYMENT === 0 ? 'CASH'
                                                                : inv.TERM_OF_PAYMENT + 'd'
                                                    }
                                                </td>
                                                <td style={{ padding: '8px 10px', textAlign: 'right', color: '#64748b' }}>{inv.BRANCH_NAME}</td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ExecutiveDashboard;
